import React, { useState } from "react";
import { C, font, btnP, btnS, card } from "../../lib/styles.js";
import { dataService } from "../../lib/dataService.js";
import { eqLabel } from "../../lib/equipment.js";

function getMessageText(msg) {
  if (!msg) return "";
  if (typeof msg.content === "string") return msg.content;
  if (Array.isArray(msg.content)) {
    return msg.content.filter((c) => c.type === "text").map((c) => c.text).join("\n");
  }
  return "";
}

function getMessageImages(msg) {
  if (!msg || !Array.isArray(msg.content)) return [];
  return msg.content.filter((c) => c.type === "image_ref" && c.url).map((c) => c.url);
}

function formatDate(iso) {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export default function FeedbackView({ session, user, onBack, onSubmitted, parentCollection = "plannerSessions" }) {
  const messages = session.conversationHistory || [];
  const report = session.report || {};
  const types = session.captureTypes || [];

  const [convAnnotations, setConvAnnotations] = useState({});
  const [reportAnnotations, setReportAnnotations] = useState({});
  const [conversationFeedback, setConversationFeedback] = useState("");
  const [reportFeedback, setReportFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const setConvAnnotation = (idx, text) => {
    setConvAnnotations((prev) => ({ ...prev, [idx]: text }));
  };

  const setReportAnnotation = (section, text) => {
    setReportAnnotations((prev) => ({ ...prev, [section]: text }));
  };

  const hasContent = () => {
    const hasConv = Object.values(convAnnotations).some((v) => v.trim());
    const hasRpt = Object.values(reportAnnotations).some((v) => v.trim());
    return hasConv || hasRpt || conversationFeedback.trim() || reportFeedback.trim();
  };

  const handleSubmit = async () => {
    if (!hasContent()) return;

    setSubmitting(true);
    setError(null);

    try {
      const convAnns = Object.entries(convAnnotations)
        .filter(([, v]) => v.trim())
        .map(([idx, text]) => ({ messageIndex: parseInt(idx), feedback: text.trim() }));

      const rptAnns = Object.entries(reportAnnotations)
        .filter(([, v]) => v.trim())
        .map(([section, text]) => ({ section, feedback: text.trim() }));

      await dataService.feedback.save(session._docId, {
        conversationAnnotations: convAnns,
        reportAnnotations: rptAnns,
        conversationFeedback: conversationFeedback.trim(),
        reportFeedback: reportFeedback.trim(),
        status: "unresolved",
        createdBy: user?.email || "unknown",
      }, parentCollection);

      onSubmitted();
    } catch (e) {
      console.error("Failed to save feedback:", e);
      setError("Failed to save feedback. Check connection and try again.");
      setSubmitting(false);
    }
  };

  // Report sections that have content
  const reportSections = [];
  if (report.summary) reportSections.push({ key: "summary", label: "Summary" });
  if (Array.isArray(report.equipment) && report.equipment.length > 0) reportSections.push({ key: "equipment", label: "Equipment" });
  if (Array.isArray(report.approach) && report.approach.length > 0) reportSections.push({ key: "approach", label: "Capture Approach" });
  if (report.notes) reportSections.push({ key: "notes", label: "Additional Notes" });

  return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>
      {/* Breadcrumb */}
      <div style={styles.breadcrumb}>
        <span style={{ cursor: "pointer" }} onClick={onBack}>Prospects</span>
        <span style={{ margin: "0 6px" }}>{"\u203A"}</span>
        <span>{formatDate(session.createdAt)}</span>
        <span style={{ margin: "0 6px" }}>{"\u203A"}</span>
        <span style={{ color: C.text }}>Give Feedback</span>
      </div>

      {/* Session header */}
      <div style={{ ...card, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 400, color: C.text }}>
            {formatDate(session.createdAt)} at {formatTime(session.createdAt)}
          </div>
          {types.length > 0 && (
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              {types.map((t) => <span key={t} style={styles.typeBadge}>{t}</span>)}
            </div>
          )}
        </div>
        <div style={{ fontSize: 11, color: C.muted, fontFamily: "'DM Mono', monospace" }}>
          {session._docId}
        </div>
      </div>

      {/* Conversation + Annotations */}
      <div style={{ ...card, padding: 0, marginBottom: 16, overflow: "hidden" }}>
        <div style={{ padding: "16px 16px 12px", borderBottom: `1px solid ${C.border}` }}>
          <div style={styles.sectionLabel}>Conversation + Annotations</div>
        </div>

        {messages.map((msg, i) => {
          const isUser = msg.role === "user";
          const text = getMessageText(msg);
          const images = getMessageImages(msg);
          if (!text && images.length === 0) return null;
          const isLast = i === messages.length - 1;

          if (isUser) {
            return (
              <div key={i} style={{ padding: "12px 16px", borderBottom: isLast ? "none" : `1px solid ${C.border}`, background: C.surface }}>
                <div style={{ ...styles.messageRole, color: C.blue }}>Visitor</div>
                {images.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: text ? 8 : 0 }}>
                    {images.map((url, j) => (
                      <img key={j} src={url} alt={`Upload ${j + 1}`} onError={(e) => { e.target.style.display = "none"; }} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 4, border: `1px solid ${C.border}` }} />
                    ))}
                  </div>
                )}
                {text && <div style={styles.messageText}>{text}</div>}
              </div>
            );
          }

          return (
            <div key={i} style={{ display: "flex", borderBottom: isLast ? "none" : `1px solid ${C.border}` }}>
              <div style={{ flex: 1, padding: "12px 16px", background: "#FAFAF8", minWidth: 0 }}>
                <div style={{ ...styles.messageRole, color: C.teal }}>Prez</div>
                <div style={styles.messageText}>{text}</div>
              </div>
              <div style={styles.annotationPanel}>
                <div style={styles.annotationLabel}>Feedback on this response</div>
                <textarea
                  style={styles.annotationInput}
                  placeholder="What should Prez do differently here?"
                  value={convAnnotations[i] || ""}
                  onChange={(e) => setConvAnnotation(i, e.target.value)}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Conversation overall feedback */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={styles.sectionLabel}>Conversation Feedback</div>
        <textarea
          style={styles.overallTextarea}
          placeholder="General notes about the conversation \u2014 what Prez got right, what needs improvement, patterns to watch for..."
          value={conversationFeedback}
          onChange={(e) => setConversationFeedback(e.target.value)}
        />
      </div>

      {/* Report + Annotations */}
      {reportSections.length > 0 && (
        <div style={{ ...card, padding: 0, marginBottom: 16, overflow: "hidden" }}>
          <div style={{ padding: "16px 16px 12px", borderBottom: `1px solid ${C.border}` }}>
            <div style={styles.sectionLabel}>Report + Annotations</div>
          </div>

          {reportSections.map((section, si) => (
            <div key={section.key} style={{ display: "flex", borderBottom: si < reportSections.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ flex: 1, padding: "12px 16px", background: "#FAFAF8", minWidth: 0 }}>
                <div style={styles.reportSectionTitle}>{section.label}</div>
                <ReportSectionContent sectionKey={section.key} report={report} />
              </div>
              <div style={styles.annotationPanel}>
                <div style={styles.annotationLabel}>Feedback on this section</div>
                <textarea
                  style={styles.annotationInput}
                  placeholder={`Notes on the ${section.label.toLowerCase()}...`}
                  value={reportAnnotations[section.key] || ""}
                  onChange={(e) => setReportAnnotation(section.key, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Report overall feedback */}
      {reportSections.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={styles.sectionLabel}>Report Feedback</div>
          <textarea
            style={styles.overallTextarea}
            placeholder="General notes about the report \u2014 accuracy, missing information, tone, things the Analyst should handle differently..."
            value={reportFeedback}
            onChange={(e) => setReportFeedback(e.target.value)}
          />
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: `${C.danger}08`, border: `1px solid ${C.danger}30`, borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontSize: 12, color: C.danger }}>
          {error}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button style={{ ...btnS, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: font }} onClick={onBack}>
          Cancel
        </button>
        <button
          style={{
            ...btnP,
            padding: "8px 20px",
            fontSize: 12,
            cursor: hasContent() && !submitting ? "pointer" : "not-allowed",
            opacity: hasContent() && !submitting ? 1 : 0.5,
            fontFamily: font,
          }}
          onClick={handleSubmit}
          disabled={!hasContent() || submitting}
        >
          {submitting ? "Saving..." : "Submit Feedback"}
        </button>
      </div>
    </div>
  );
}

// --- Report Section Content ---

function ReportSectionContent({ sectionKey, report }) {
  switch (sectionKey) {
    case "summary":
      return <div style={styles.reportBody}>{report.summary}</div>;

    case "equipment":
      return (
        <div style={{ ...styles.reportBody, display: "flex", flexDirection: "column", gap: 4 }}>
          {report.equipment.map((eq, i) => (
            <div key={i}>
              <span style={{ fontWeight: 400 }}>{eqLabel(eq)}</span>
              {(eq.role || eq.description || eq.reasoning) && (
                <span style={{ color: C.muted }}> {"\u2014"} {eq.role || eq.description || eq.reasoning}</span>
              )}
              {eq.type && <span style={{ color: C.muted, fontSize: 11 }}> ({eq.type})</span>}
            </div>
          ))}
        </div>
      );

    case "approach":
      return (
        <div style={{ ...styles.reportBody, display: "flex", flexDirection: "column", gap: 10 }}>
          {report.approach.map((step, i) => {
            const title = typeof step === "string" ? step : (step.title || step.step || step.name || "");
            const detail = typeof step === "string" ? "" : (step.detail || step.description || "");
            return (
              <div key={i}>
                <div style={{ fontWeight: 400 }}>{i + 1}. {title}</div>
                {detail && <div style={{ color: C.muted, marginTop: 2, lineHeight: 1.5 }}>{detail}</div>}
              </div>
            );
          })}
        </div>
      );

    case "notes":
      return <div style={{ ...styles.reportBody, whiteSpace: "pre-wrap" }}>{report.notes}</div>;

    default:
      return null;
  }
}

const styles = {
  breadcrumb: {
    fontSize: 11,
    color: C.muted,
    fontWeight: 400,
    marginBottom: 12,
    fontFamily: font,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: C.muted,
  },
  typeBadge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 500,
    background: `${C.teal}12`,
    color: C.teal,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  messageRole: {
    fontSize: 10,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: 6,
  },
  messageText: {
    fontSize: 13,
    fontWeight: 300,
    color: C.text,
    lineHeight: 1.5,
    whiteSpace: "pre-wrap",
    maxWidth: "65ch",
  },
  annotationPanel: {
    width: 260,
    flexShrink: 0,
    padding: 12,
    background: C.bg,
    borderLeft: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  annotationLabel: {
    fontSize: 10,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: C.muted,
  },
  annotationInput: {
    width: "100%",
    minHeight: 60,
    padding: "8px 10px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontFamily: font,
    fontSize: 12,
    fontWeight: 300,
    color: C.text,
    resize: "vertical",
    outline: "none",
    boxSizing: "border-box",
  },
  overallTextarea: {
    width: "100%",
    minHeight: 100,
    padding: "10px 13px",
    background: C.surfaceAlt,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontFamily: font,
    fontSize: 13,
    fontWeight: 300,
    color: C.text,
    resize: "vertical",
    outline: "none",
    marginTop: 8,
    boxSizing: "border-box",
  },
  reportSectionTitle: {
    fontSize: 10,
    fontWeight: 400,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: C.muted,
    marginBottom: 6,
  },
  reportBody: {
    fontSize: 13,
    fontWeight: 300,
    color: C.text,
    lineHeight: 1.5,
    maxWidth: "65ch",
  },
};
